import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const AnnouncementPopup = ({ announcement, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Delay showing popup slightly for better UX
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  if (!announcement) return null;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onClose) onClose(announcement.id);
    }, 200);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-200 ${
          isVisible ? 'scale-100' : 'scale-95'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 bg-black/10 hover:bg-black/20 rounded-full transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Image */}
        {announcement.imageUrl && (
          <div className="w-full h-48 bg-gray-100">
            <img
              src={announcement.imageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            {announcement.title}
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {announcement.content}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            {announcement.linkUrl ? (
              <>
                <a
                  href={announcement.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-mint-500 hover:bg-mint-600">
                    {announcement.linkText || 'Learn More'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1"
                >
                  Dismiss
                </Button>
              </>
            ) : (
              <Button
                onClick={handleClose}
                className="w-full bg-mint-500 hover:bg-mint-600"
              >
                Got it!
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPopup;
